/* Starlog cover archive modal.
 * Click any cover -> open <dialog> with full image + contextual blurb.
 * Keyboard: esc closes (native dialog behavior), left/right navigates,
 * focus returns to the originating cover trigger on close.
 *
 * Designed to be idempotent — re-running init() is safe.
 */
(function () {
  if (typeof window === "undefined") return;

  function init(root) {
    var archive = root || document;
    var sections = archive.querySelectorAll(
      ".starlog-archive:not([data-archive-modal-bound])"
    );
    if (!sections.length) return;

    sections.forEach(function (section) {
      var triggers = Array.prototype.slice.call(
        section.querySelectorAll("[data-cover-modal-trigger]")
      );
      var modal = section.querySelector("[data-archive-modal]");
      if (!modal || !triggers.length) return;

      // Resolve elements once.
      var imgEl = modal.querySelector("[data-archive-modal-image]");
      var yearEl = modal.querySelector("[data-archive-modal-year]");
      var issueEl = modal.querySelector("[data-archive-modal-issue]");
      var captionEl = modal.querySelector("[data-archive-modal-caption]");
      var contextEl = modal.querySelector("[data-archive-modal-context]");
      var counterEl = modal.querySelector("[data-archive-modal-counter]");
      var closeBtn = modal.querySelector("[data-archive-modal-close]");
      var prevBtn = modal.querySelector("[data-archive-modal-prev]");
      var nextBtn = modal.querySelector("[data-archive-modal-next]");

      var currentIndex = -1;
      var lastFocused = null;

      function populate(index) {
        if (index < 0 || index >= triggers.length) return;
        currentIndex = index;
        var t = triggers[index];

        var imageUrl = t.getAttribute("data-cover-image") || "";
        var year = t.getAttribute("data-cover-year") || "";
        var caption = t.getAttribute("data-cover-caption") || "";
        var issue = t.getAttribute("data-cover-issue") || "";
        var context = t.getAttribute("data-cover-context") || "";

        // Image — use the original cover image inside the trigger as
        // a fallback if the explicit data-cover-image is empty.
        if (imageUrl) {
          imgEl.src = imageUrl;
        } else {
          var srcImg = t.querySelector("img");
          imgEl.src = srcImg ? srcImg.src : "";
        }
        imgEl.alt = (year + " Starlog cover").trim();

        yearEl.textContent = year;
        issueEl.textContent = issue;
        captionEl.textContent = caption;
        contextEl.innerHTML = context; // richtext field, sanitized at editor time
        counterEl.textContent =
          String(index + 1) + " / " + triggers.length;

        // Visibility of nav buttons: always show; disable at edges.
        prevBtn.disabled = index === 0;
        nextBtn.disabled = index === triggers.length - 1;
      }

      function open(index) {
        lastFocused = document.activeElement;
        populate(index);
        if (typeof modal.showModal === "function") {
          modal.showModal();
        } else {
          // Fallback for browsers without <dialog> support.
          modal.setAttribute("open", "");
          modal.style.display = "flex";
        }
        // Lock body scroll while modal is open.
        document.documentElement.classList.add(
          "starlog-archive-modal-open"
        );
        // Focus the close button so esc / enter is wired up immediately.
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      }

      function close() {
        if (typeof modal.close === "function") {
          modal.close();
        } else {
          modal.removeAttribute("open");
          modal.style.display = "";
        }
        document.documentElement.classList.remove(
          "starlog-archive-modal-open"
        );
        if (lastFocused && typeof lastFocused.focus === "function") {
          lastFocused.focus({ preventScroll: true });
        }
        currentIndex = -1;
      }

      function next() {
        if (currentIndex < triggers.length - 1) {
          populate(currentIndex + 1);
        }
      }

      function prev() {
        if (currentIndex > 0) {
          populate(currentIndex - 1);
        }
      }

      // Bind triggers
      triggers.forEach(function (t, i) {
        t.addEventListener("click", function (e) {
          e.preventDefault();
          open(i);
        });
      });

      // Bind buttons
      if (closeBtn) closeBtn.addEventListener("click", close);
      if (prevBtn) prevBtn.addEventListener("click", prev);
      if (nextBtn) nextBtn.addEventListener("click", next);

      // Backdrop click closes (clicking on the dialog element itself,
      // not the inner content)
      modal.addEventListener("click", function (e) {
        if (e.target === modal) close();
      });

      // Keyboard nav inside the modal
      modal.addEventListener("keydown", function (e) {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          next();
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          prev();
        }
      });

      // <dialog> esc handling fires a "cancel" event — clean up state
      modal.addEventListener("cancel", function () {
        document.documentElement.classList.remove(
          "starlog-archive-modal-open"
        );
        if (lastFocused && typeof lastFocused.focus === "function") {
          lastFocused.focus({ preventScroll: true });
        }
        currentIndex = -1;
      });

      section.setAttribute("data-archive-modal-bound", "true");
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }

  // Re-bind on Shopify theme editor section reloads.
  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });

  /* ------------------------------------------------------------
     Vertical-wheel passthrough on the horizontal cover strip.

     The cover strip is a horizontal scroll-snap container. By default,
     a trackpad's vertical-dominant wheel gesture (the normal "scroll
     down the page" motion) gets eaten by the strip and converted to
     horizontal-axis scroll inside it, blocking the page from advancing.

     Pattern: on every wheel event over the strip, compare |deltaY| to
     |deltaX|. If vertical is dominant, the user wants to scroll the
     page — forward deltaY to window.scrollBy and preventDefault on the
     native horizontal scroll. If horizontal is dominant, let the native
     scroll handle it.
     ------------------------------------------------------------ */
  function bindWheelPassthrough(root) {
    var scope = root || document;
    var strips = scope.querySelectorAll(
      "[data-archive-strip]:not([data-wheel-passthrough-bound])"
    );
    strips.forEach(function (strip) {
      strip.setAttribute("data-wheel-passthrough-bound", "true");
      strip.addEventListener(
        "wheel",
        function (e) {
          // ANY vertical motion -> forward to page scroll and block the
          // carousel from grabbing the event. The earlier "vertical
          // dominant" check failed too often: trackpads emit small
          // horizontal jitter alongside vertical scroll, which made the
          // carousel intermittently swallow scroll events and the page
          // appeared to scroll slowly / get stuck.
          //
          // Pure horizontal wheel/swipe (deltaY === 0) still falls
          // through to the native carousel scroll, so users who
          // genuinely want to browse covers sideways with a trackpad
          // swipe still can. Touch swipe is unaffected (touch events,
          // not wheel events).
          if (e.deltaY !== 0) {
            // Use the options form with `behavior: "instant"`. The
            // legacy 2-arg form was supposed to bypass CSS smooth-scroll,
            // but Chrome on Mac kept inheriting `scroll-behavior: smooth`
            // from html and queueing a smooth animation per forwarded
            // wheel event — producing the "scrolls slowly / gets stuck"
            // feel users reported. `behavior: "instant"` is explicit and
            // cross-browser (Chrome 96+, Safari 15.4+, Firefox 98+) and
            // cannot inherit. Paired with the strip's CSS being switched
            // from `scroll-snap-type: x mandatory` to `x proximity` so
            // Chrome's snap engine stops fighting our preventDefault.
            window.scrollBy({ left: 0, top: e.deltaY, behavior: "instant" });
            e.preventDefault();
          }
        },
        { passive: false }
      );
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      bindWheelPassthrough();
    });
  } else {
    bindWheelPassthrough();
  }
  document.addEventListener("shopify:section:load", function (e) {
    bindWheelPassthrough(e.target);
  });
})();
